// Nutrient Calculator - Light Feeding Schedule
// Updated: 06-01-2026 based on "Nutrient Calculator NHC Update.xlsb.xlsx" -> "Light Feeding Calculator" Tab
// Includes custom scaled-down rates and reduced Target PPMs for a light feeding cycle.

const config = {
    // Rates in mL per gallon or Grams per gallon (for MOAB)
    // Indices: 0:Veg1, 1:Veg2, 2:Veg3, 3:B1, 4:B2, 5:B3, 6:B4, 7:B5, 8:B6, 9:B7, 10:B8, 11:Flush
    nutrients: {
        "BALANCE":             [0.0, 0.0,  0.0,      0.5,    0.5,    0.75,   1.0,    1.0,    2.0,    1.5, 0.0, 0.0],
        "COCO CAL":            [1.5, 1.85, 2.2916,   2.2916, 2.2916, 2.2916, 2.2916, 2.2916, 2.2916, 2.0, 1.0, 1.0],
        "VEG A":               [1.5, 1.85, 2.2916,   0.0,    0.0,    0.0,    0.0,    0.0,    0.0,    0.0, 0.0, 0.0],
        "VEG B":               [1.5, 1.85, 2.2916,   0.0,    0.0,    0.0,    0.0,    0.0,    0.0,    0.0, 0.0, 0.0],
        "BLOOM A":             [0.0, 0.0,  0.0,      2.75,   3.25,   4.5,    4.5,    5.25,   4.5,    3.5, 3.0, 0.0],
        "BLOOM B":             [0.0, 0.0,  0.0,      2.75,   3.25,   4.5,    4.5,    5.25,   4.5,    3.5, 3.0, 0.0],
        "BOOSTER - Mid":       [0.0, 0.0,  0.0,      0.3855, 1.1563, 1.5,    2.0,    2.5,    2.25,   2.0, 1.5, 0.0],
        "BOOSTER - Late Grams":[0.0, 0.0,  0.0,      0.0,    0.0,    0.0,    0.0,    0.0,    1.0,    1.5, 0.5, 0.0], // Bloom 1 Moab is inactive
        "ENZYME":              [2.5, 2.5,  2.5,      2.5,    2.5,    2.5,    2.5,    0.0,    0.0,    0.0, 0.0, 0.0],
        "Beneficial":          [0.5, 0.5,  0.5,      0.0,    0.0,    0.0,    0.0,    0.0,    0.0,    0.0, 0.0, 0.0]
    },
    // Target PPM values (Veg 1 updated to 150, Bloom 1-6 are scaled down for Light Feeding)
    target_ppm_500: [150, 200, 300, 300, 375, 475, 500, 550, 600, 550, 400, 30],
    
    // Addback formulas activation flags
    // Determines if a nutrient should be calculated for "Add Back" (Rows 30-36 in Excel)
    addback_formulas: {
        "BALANCE":             [false, false, false, false, false, false, false, false, false, false, false, false],
        "COCO CAL":            [true,  true,  true,  true,  true,  true,  true,  true,  true,  true,  true,  false], // Flush is Inactive
        "VEG A":               [true,  true,  true,  false, false, false, false, false, false, false, false, false],
        "VEG B":               [true,  true,  true,  false, false, false, false, false, false, false, false, false],
        "BLOOM A":             [false, false, false, true,  true,  true,  true,  true,  true,  true,  true,  false],
        "BLOOM B":             [false, false, false, true,  true,  true,  true,  true,  true,  true,  true,  false],
        "BOOSTER - Mid":       [false, false, false, true,  true,  true,  true,  true,  true,  true,  true,  false],
        "BOOSTER - Late Grams":[false, false, false, false, false, false, false, false, true,  true,  true,  false]
    }
};

const columnMap = {
    "VEG 1": 0, "VEG 2": 1, "VEG 3": 2,
    "BLOOM 1": 3, "BLOOM 2": 4, "BLOOM 3": 5, "BLOOM 4": 6,
    "BLOOM 5": 7, "BLOOM 6": 8, "BLOOM 7": 9, "BLOOM 8": 10,
    "Flush": 11
};

/**
 * Calculates the amount of each nutrient to add back based on system volume and current PPM.
 * Formula: ((targetPPM - currentPPM) / targetPPM) * (systemVolume * ratePerGallon)
 * 
 * @param {number} systemVolume - Total system volume in gallons
 * @param {number} currentPPM - Current measured PPM (500 scale)
 * @param {string} columnName - Growth phase name (e.g., "VEG 1", "BLOOM 3")
 * @returns {object} - Calculated ml/grams for each nutrient
 */
export function calculateNutrientColumn(systemVolume, currentPPM, columnName) {
    const columnIndex = columnMap[columnName];
    if (columnIndex === undefined) return {};
    
    const targetPPM = config.target_ppm_500[columnIndex];
    const result = {};

    Object.keys(config.nutrients).forEach((nutrientKey) => {
        const isApplicable = config.addback_formulas[nutrientKey] && config.addback_formulas[nutrientKey][columnIndex];
        const rate = config.nutrients[nutrientKey][columnIndex];
        
        if (isApplicable && rate > 0) {
            const baseAmount = systemVolume * rate;
            // Formula: ((Target - Current) / Target) * FullSwapAmount
            const calculated = Math.round(((targetPPM - currentPPM) / targetPPM) * baseAmount);
            result[nutrientKey] = calculated;
        } else {
            result[nutrientKey] = "";
        }
    });

    return result;
}

export const nutrientColumnNames = Object.keys(columnMap);
